import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Download, Building2, Star, RefreshCw, MapPin, Trash2,
  CheckSquare, DollarSign, Users, ArrowRight, Send, Flame,
} from "lucide-react";
import { LeadMetaRow } from "@/components/prospects/LeadMetaRow";
import { OwnerFilter } from "@/components/prospects/OwnerFilter";

export function ProspectPipelineView({
  savedCompanies, selectedSaved, toggleSavedSelect, toggleAllSaved,
  handleBulkSync, bulkSyncing, handleExport, handleBulkDelete, isUnread, navigate,
  isAdmin, team, ownerFilter, setOwnerFilter, savedTotal, pipelineLoading,
}) {
  return (
    <>
      {isAdmin && (
        <div data-testid="pipeline-toolbar" className="flex items-center justify-between">
          <OwnerFilter team={team} value={ownerFilter} onChange={setOwnerFilter} />
          <span data-testid="pipeline-count" className="text-xs text-muted-foreground flex items-center gap-1.5">
            {pipelineLoading && <RefreshCw data-testid="pipeline-loading" className="w-3 h-3 animate-spin" />}
            {savedTotal} lead{savedTotal === 1 ? "" : "s"}
          </span>
        </div>
      )}
      {selectedSaved.size > 0 && (
        <div className="flex items-center gap-3 p-2.5 bg-rose-50 border border-rose-200 rounded-lg animate-fade-in">
          <CheckSquare className="w-4 h-4 text-rose-600" />
          <span className="text-xs font-medium text-rose-700">{selectedSaved.size} selected</span>
          <div className="ml-auto flex gap-1.5">
            <Button data-testid="bulk-sync-monday-btn" size="sm" onClick={handleBulkSync} disabled={bulkSyncing}
              className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
              {bulkSyncing ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Sync to Monday ({selectedSaved.size})
            </Button>
            <Button data-testid="bulk-export-btn" size="sm" variant="outline" onClick={handleExport} className="h-7 text-xs"><Download className="w-3 h-3 mr-1" /> Export</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="bulk-delete-btn" size="sm" variant="destructive" className="h-7 text-xs"><Trash2 className="w-3 h-3 mr-1" /> Delete ({selectedSaved.size})</Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="bulk-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {selectedSaved.size} prospect{selectedSaved.size > 1 ? "s" : ""}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes the selected prospect{selectedSaved.size > 1 ? "s" : ""} along with their audits and CRM sync history. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="bulk-delete-confirm" onClick={handleBulkDelete} className="bg-rose-600 hover:bg-rose-700 text-white">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      {savedCompanies.length > 0 ? (
        <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 transition-opacity ${pipelineLoading ? "opacity-50" : ""}`}>
          {savedCompanies.map((c, i) => (
            <Card key={c.company_id}
              data-testid={`saved-company-${c.company_id}`}
              className={`border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group ${selectedSaved.has(i) ? "ring-2 ring-indigo-300" : isUnread(c) ? "ring-1 ring-rose-300" : ""}`}>
              <CardContent className="p-0">
                {/* Card header */}
                <div className="p-3 pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2.5 min-w-0 flex-1" onClick={() => navigate(`/companies/${c.company_id}`)}>
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Building2 className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold leading-tight truncate">{c.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                          <Badge variant="outline" className="text-[9px] h-4">{c.category}</Badge>
                          <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{c.city}, {c.state}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Checkbox checked={selectedSaved.has(i)} onCheckedChange={() => toggleSavedSelect(i)} className="mr-1" />
                      {(c.priority_score || 0) >= 80 && (
                        <Badge data-testid="hot-lead-badge" className="text-[9px] h-4 bg-amber-500 text-white border-0 flex items-center gap-0.5">
                          <Flame className="w-2.5 h-2.5" />Hot
                        </Badge>
                      )}
                      {isUnread(c) && (
                        <Badge data-testid="new-lead-badge" className="text-[9px] h-4 bg-rose-500 text-white border-0">New</Badge>
                      )}
                      {c.assignment_status === "unassigned" && (
                        <Badge variant="outline" className="text-[9px] h-4 bg-rose-50 text-rose-700 border-rose-200">Unassigned</Badge>
                      )}
                      <Badge variant="outline"
                        className={`capitalize text-[9px] h-4 ${
                          c.status === "new" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                          c.status === "contacted" ? "bg-amber-50 text-amber-700 border-amber-200" :
                          c.status === "qualified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          "bg-slate-50 text-slate-500 border-slate-200"
                        }`}>{c.status}</Badge>
                    </div>
                  </div>
                </div>

                {/* Scores row */}
                <div className="px-3 py-2 bg-slate-50/60 border-t border-b border-slate-100 grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: "Web", score: c.website_score },
                    { label: "Presence", score: c.presence_score },
                    { label: "Priority", score: c.priority_score },
                  ].map((s) => (
                    <div key={s.label}>
                      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
                      <p className={`text-sm font-bold ${
                        s.score == null ? "text-slate-300" :
                        s.score >= 70 ? "text-emerald-600" : s.score >= 40 ? "text-amber-600" : "text-rose-600"
                      }`}>{s.score ?? "--"}</p>
                    </div>
                  ))}
                </div>

                {/* Quick info + needs */}
                <div className="p-3 pt-2" onClick={() => navigate(`/companies/${c.company_id}`)}>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground mb-2">
                    {c.revenue_label && <span className="flex items-center gap-0.5"><DollarSign className="w-2.5 h-2.5" />{c.revenue_label}</span>}
                    {c.employee_count && <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{c.employee_count} emp</span>}
                    <span className="flex items-center gap-0.5"><Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />{c.google_rating}</span>
                  </div>
                  {c.digital_needs && c.digital_needs.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {c.digital_needs.slice(0, 3).map((need, ni) => (
                        <span key={ni} className="text-[9px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-100">{need}</span>
                      ))}
                      {c.digital_needs.length > 3 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500">+{c.digital_needs.length - 3}</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-end mt-2">
                    <span className="text-[10px] text-indigo-600 font-medium flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      View details <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
                <LeadMetaRow company={c} />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {ownerFilter && ownerFilter !== "all" ? "No leads for this user." : "No saved prospects. Search and save to build your pipeline."}
          </CardContent>
        </Card>
      )}
    </>
  );
}
